import { useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { MdRefresh } from 'react-icons/md';
import { Icon } from '@/components/v4';
import AGGridMain from '../../../../components/tables/AGGrid/AGGridMain';
import useColumnState from '../../../../hooks/useColumnState';
import { useQuery } from 'hooks/useCustomQuery';
import axios from 'axios';
import config from '../../../../config.json';
import ModalApprovalContent from '../ModalApprovalContent';
import ModalEditContent from './InvoiceLineEdit';
import InvoiceLinesAdd from './add/InvoiceLinesAdd';
import ItemCodeDisplay from './formFields/display/ItemCodeDisplay';
import DescriptionDisplay from './formFields/display/DescriptionDisplay';
import QuantityDisplay from './formFields/display/QuantityDisplay';
import UnitPriceDisplay from './formFields/display/UnitPriceDisplay';
import LineTotalDisplay from './formFields/display/LineTotalDisplay';
import {
    Box,
    Button,
    HStack,
    IconButton,
    Tooltip,
    useToast,
} from '@chakra-ui/react';
import { Text } from '@chakra-ui/react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { NegativeSixDP } from 'components/maintenance/NumberFormatCustom';
import walkthroughIds from '../walkthroughIds';
import BreadcrumbEntityDropdown from 'components/pickers/BreadcrumbEntityDropdown';
// import { DeleteIcon } from '@chakra-ui/icons';

const EDITABLE_STATUS = [0, 1, 8];
/*
 * Component rendered when somoene clicks the invoice lines button on the Invoices.jsx component
 */
const InvoiceLines = ({
    invoice,
    invoices = [],
    onInvoiceChange,
    onBackToInvoices,
    config: configProp,
}) => {
    const toast = useToast();
    // Local modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('');
    const [modalData, setModalData] = useState(null);
    const [modalState, setModalState] = useState({});
    const [apiError, setApiError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // scope from state
    const { requiredScope } = useSelector((state) => {
        return { ...state.login };
    });

    // table data from state
    const { invoiceType } = useSelector((state) => state.table);
    const { darkMode } = useSelector((state) => state.settings);
    const { invoiceLines } = walkthroughIds;
    // AGGrid columns definition with Actions column
    const baseColumns = useMemo(
        () => [
            {
                headerName: 'Date/Time (UTC)',
                field: 'createTS',
                flex: 1,
                sortable: true,
                valueFormatter: ({ value }) =>
                    value ? new Date(value).toLocaleString() : '',
            },
            {
                headerName: 'Item Code',
                field: 'itemCode',
                flex: 1,
                valueFormatter: ({ value }) => value || '',
            },
            {
                headerName: 'Unit Price',
                field: 'unitPrice',
                flex: 1,
                cellRenderer: ({ value }) => (
                    <NegativeSixDP displayType="text" value={value} />
                ),
            },
            {
                headerName: 'Qty',
                field: 'quantity',
                flex: 1,
            },
            {
                headerName: 'Line Total',
                field: 'lineTotal',
                flex: 1,
                cellRenderer: ({ value }) => (
                    <NegativeSixDP displayType="text" value={value} />
                ),
            },
            {
                headerName: 'Description',
                field: 'description',
                flex: 2,
            },
            {
                headerName: 'Action',
                lockPosition: 'right',
                width: 120,
                sortable: false,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                cellRenderer: ({ data }) => {
                    if (!EDITABLE_STATUS.includes(invoice?.invoiceStatus)) {
                        return null;
                    }
                    return (
                        <Tooltip label="Edit">
                            <IconButton
                                icon={
                                    <BsThreeDotsVertical
                                        fill={darkMode ? 'white' : 'black'}
                                    />
                                }
                                aria-label="Edit"
                                variant="ghost"
                                onClick={() => openModal('Edit', data)}
                                _hover={{
                                    bg: darkMode
                                        ? 'whiteAlpha.200'
                                        : 'blackAlpha.100',
                                }}
                                _active={{
                                    bg: darkMode
                                        ? 'whiteAlpha.300'
                                        : 'blackAlpha.200',
                                }}
                                _focus={{ boxShadow: 'none' }}
                            />
                        </Tooltip>
                    );
                },
            },
        ],
        [darkMode, invoice?.invoiceStatus],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);
    // Debug: log data and columns

    // Fetch invoice lines data using TanStack useQuery
    const {
        data: invoiceLinesData,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: ['invoiceLines', invoice?.id],
        queryFn: async () => {
            if (!invoice?.id) return [];

            // First fetch to get total count
            const firstRes = await axios.get(`/invoicelines/${invoice.id}`, {
                params: { page: 0, limit: 9999 },
            });

            const total = firstRes.data.total || 0;

            // If there are more records, fetch all pages including first one
            if (total > 9999) {
                const totalPages = Math.ceil(total / 9999);
                const allPages = [];

                for (let page = 0; page < totalPages; page++) {
                    allPages.push(
                        axios.get(`/invoicelines/${invoice.id}`, {
                            params: { page, limit: 9999 },
                        }),
                    );
                }

                // Fetch all pages in parallel and wait for all to complete
                const responses = await Promise.all(allPages);
                const allInvoiceLines = [];
                responses.forEach((res) => {
                    allInvoiceLines.push(...(res.data.invoiceLines || []));
                });

                return allInvoiceLines;
            }

            // If total is less than 9999, just return the first page data
            return firstRes.data.invoiceLines || [];
        },
        enabled: !!invoice?.id,
    });
    // Total of the lines on the UI
    const currentTotal = useMemo(() => {
        const arr = Array.isArray(invoiceLinesData) ? invoiceLinesData : [];
        const total = arr.reduce((result, currentValue) => {
            result = result + (currentValue?.lineTotal ?? 0);
            return result;
        }, 0);
        return Math.round(total * 100) / 100;
    }, [invoiceLinesData]);

    const tableDataWithColumns = useMemo(
        () => ({
            columns,
            data: Array.isArray(invoiceLinesData) ? invoiceLinesData : [],
        }),
        [columns, invoiceLinesData],
    );

    const scopeCanEdit = requiredScope > [20, 40, 60][invoiceType];
    /*
     * Component for the Invoice Total and Update Status button at top of hte invoice lines component
     */
    // const titleProps = (
    //     <Box className="flex flex-row justify-between gap-1 w-full">
    //         <TextField
    //             disabled
    //             className="mt-1"
    //             label="Invoice Total"
    //             value={currentTotal}
    //             InputProps={{
    //                 inputComponent: NegativeSixDP,
    //             }}
    //             data-walkthroughid={invoiceLines.ufInvoiceTotalInput}
    //         />
    //     </Box>
    // );

    // set current invoice use Effect
    // useEffect(() => {
    //     if (invoice?.id) {
    //         const tmp = parent.find((v) => v.id === invoice?.id);
    //         setCurrentInvoice(tmp);
    //     }
    // }, [invoice?.id]);

    // Modal handlers (refactored for Add/Edit)
    const openModal = (mode, data = null) => {
        setModalMode(mode);
        if (mode === 'Add') {
            setModalData(null);
            setModalState({});
        } else if (mode === 'Edit') {
            setModalData(data);
            setModalState(data ? { ...data } : {});
        } else if (mode === 'Delete') {
            setModalData(data);
            setModalState({});
        } else if (mode === 'Approval') {
            // Fallback: if invoiceStatus is missing, do not open modal
            // if (!data || typeof data.invoiceStatus === 'undefined') {
            //     return;
            // }
            setModalData(data);
            // Default to first valid transition for 'To' dropdown
            const statusOptions =
                config.invoices.statusUpload[data.invoiceStatus] || [];
            setModalState({
                upload: statusOptions.length > 0 ? statusOptions[0][0] : '',
            });
        }
        setModalOpen(true);
    };
    const closeModal = () => {
        setModalOpen(false);
        setModalMode('');
        setModalData(null);
        setModalState({});
    };

    // Modal submit logic
    const handleModalSubmit = async (submitData) => {
        let res;
        try {
            // Handle delete operation from the form's delete button
            if (submitData === undefined && modalMode === 'Edit') {
                setIsDeleting(true);
                res = await axios.delete(`/invoiceline/${modalData?.id}`);
                closeModal();
                refetch();
                return;
            }

            if (modalMode === 'Delete') {
                setIsDeleting(true);
            } else {
                setIsSaving(true);
            }

            switch (modalMode) {
                case 'Add':
                    res = await axios.post('/invoiceline/', {
                        ...submitData,
                        invoiceID: invoice?.id,
                    });
                    if (res?.data?.error) {
                        setApiError(res.data.message || res.data.error);
                        return;
                    }
                    break;
                case 'Edit': {
                    // Send all fields from modalData, merged with form changes
                    const updatePayload = { ...modalData, ...submitData };
                    res = await axios.put('/invoiceline', updatePayload);
                    if (res?.data?.error) {
                        setApiError(res.data.error);
                        return;
                    }
                    break;
                }
                case 'Delete':
                    res = await axios.delete(`/invoiceline/${modalData?.id}`);
                    break;
                case 'Approval':
                    res = await axios.post(
                        `/invoice/${modalData?.id}/${modalState?.upload}`,
                    );
                    break;
                default:
                    break;
            }
            closeModal();
            refetch();
        } catch (error) {
            setApiError(error?.response?.data?.message || 'An error occurred');
        } finally {
            setIsSaving(false);
            setIsDeleting(false);
        }
    };

    // AGGrid header actions
    const additionalActions = [
        {
            icon: () => (
                <Icon
                    as={MdRefresh}
                    sx={{ boxSize: '24px' }}
                    data-walkthroughid={invoiceLines.ufRefreshIconBtn}
                />
            ),
            tooltip: 'Refresh',
            position: 'toolbar',
            onClick: () => refetch(),
        },
    ];

    const gridRef = useRef();

    // Invoice dropdown for header
    const InvoiceDropdown = () => (
        <BreadcrumbEntityDropdown
            items={invoices}
            selectedId={invoice?.id}
            onSelect={onInvoiceChange}
            darkMode={darkMode}
            placeholder="Select Invoice"
            width="380px"
            getLabel={(inv) => {
                const statusLabel =
                    configProp?.invoices?.statusJSON?.[inv.invoiceStatus] ||
                    inv.invoiceStatus;
                return `${
                    inv.invoiceNumber ? `#${inv.invoiceNumber}` : inv.id
                } - ${inv.entityName || inv.parentName || ''} - ${statusLabel}`;
            }}
        />
    );

    return (
        <>
            <>
                {/* Header - always visible */}
                <HStack spacing={3} mb={4} alignItems="center">
                    <Text
                        as="span"
                        sx={{
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            _hover: { textDecoration: 'underline' },
                        }}
                        onClick={onBackToInvoices}>
                        Invoices
                    </Text>
                    <Text
                        sx={{
                            fontFamily: 'Inter',
                            fontSize: '14px',
                            fontWeight: 'normal',
                            color: darkMode ? '#FFFFFF' : '#262626',
                        }}>
                        &gt;
                    </Text>
                    {modalMode === 'Add' || modalMode === 'Edit' ? (
                        <>
                            <InvoiceDropdown />
                            <Text
                                sx={{
                                    fontFamily: 'Inter',
                                    fontSize: '14px',
                                    fontWeight: 'normal',
                                    color: darkMode ? '#FFFFFF' : '#262626',
                                }}>
                                &gt;
                            </Text>
                            <Text
                                as="span"
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    _hover: { textDecoration: 'underline' },
                                }}
                                onClick={closeModal}>
                                Invoice Lines
                            </Text>
                            <Text
                                sx={{
                                    fontFamily: 'Inter',
                                    fontSize: '14px',
                                    fontWeight: 'normal',
                                    color: darkMode ? '#FFFFFF' : '#262626',
                                }}>
                                &gt;
                            </Text>
                            <Text
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}>
                                {modalMode === 'Add'
                                    ? 'Add Invoice Lines'
                                    : 'Edit Invoice Line'}
                            </Text>
                        </>
                    ) : (
                        <>
                            <Text
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: '500',
                                }}>
                                Invoice Lines
                            </Text>
                            <Text
                                sx={{
                                    fontFamily: 'Inter',
                                    fontSize: '14px',
                                    fontWeight: 'normal',
                                    color: darkMode ? '#FFFFFF' : '#262626',
                                }}>
                                &gt;
                            </Text>
                            <InvoiceDropdown />
                        </>
                    )}
                </HStack>
                {/* Render Add as new multi-row table, Edit as form, Approval/Delete as modal */}
                {modalMode === 'Add' ? (
                    <InvoiceLinesAdd
                        invoice={invoice}
                        existingLines={invoiceLinesData}
                        onBack={closeModal}
                        onSuccess={() => {
                            closeModal();
                            refetch();
                        }}
                    />
                ) : modalMode === 'Edit' ? (
                    <>
                        {apiError && (
                            <Box color="red.500" mb={2}>
                                {apiError}
                            </Box>
                        )}
                        <ModalEditContent
                            mode={modalMode.toLowerCase()}
                            state={modalState}
                            onClose={closeModal}
                            onBack={closeModal}
                            darkMode={darkMode}
                            onSubmit={handleModalSubmit}
                            onDelete={() => handleModalSubmit(undefined)}
                            isLoading={isSaving}
                            isDeleting={isDeleting}
                        />
                    </>
                ) : (
                    <>
                        <Text
                            sx={{
                                fontSize: '16px',
                                fontWeight: '500',
                                marginBottom: '15px',
                            }}>
                            Invoice Total : {currentTotal}
                        </Text>
                        <AGGridMain
                            gridRef={gridRef}
                            tableData={tableDataWithColumns}
                            additionalActions={additionalActions}
                            refetch={refetch}
                            onColumnVisible={onColumnVisible}
                            pagination={true}
                            paginationPageSize={100}
                            paginationPageSizeSelector={[
                                50, 100, 250, 500, 1000,
                            ]}
                            suppressPaginationPanel={false}
                            rowBuffer={20}
                            suppressRowVirtualisation={false}
                            titleName="Invoice Lines"
                            useFixedHeight={false}
                            customHeight="calc(100vh - 250px)"
                            extraComponents={
                                EDITABLE_STATUS.includes(
                                    invoice?.invoiceStatus,
                                ) &&
                                scopeCanEdit && (
                                    <div style={{ paddingLeft: '20px' }}>
                                        <HStack spacing={2}>
                                            <Tooltip
                                                label="Add Invoice Line"
                                                placement="bottom"
                                                hasArrow
                                                portalProps={{
                                                    containerRef: undefined,
                                                }}>
                                                <div
                                                    style={{
                                                        position: 'relative',
                                                        display: 'inline-block',
                                                    }}>
                                                    {' '}
                                                    <Button
                                                        size="sm"
                                                        width="130px"
                                                        onClick={() =>
                                                            openModal('Add')
                                                        }>
                                                        + Add Line
                                                    </Button>
                                                </div>
                                            </Tooltip>
                                            {invoice?.invoiceStatus !== 8 &&
                                                scopeCanEdit && (
                                                    <Tooltip
                                                        label="Update Invoice Status"
                                                        placement="bottom"
                                                        hasArrow
                                                        portalProps={{
                                                            containerRef:
                                                                undefined,
                                                        }}>
                                                        <div>
                                                            <Button
                                                                size="sm"
                                                                width="130px"
                                                                onClick={() =>
                                                                    openModal(
                                                                        'Approval',
                                                                        invoice,
                                                                    )
                                                                }>
                                                                Update Status
                                                            </Button>
                                                        </div>
                                                    </Tooltip>
                                                )}
                                        </HStack>
                                    </div>
                                )
                            }
                            isLoading={isLoading}
                        />
                        {Array.isArray(invoiceLinesData) &&
                            invoiceLinesData.length === 0 &&
                            !isLoading && (
                                <Box textAlign="center" color="gray.500" mt={4}>
                                    No invoice lines found.
                                </Box>
                            )}
                        {/* Modal rendering logic for Approval/Delete only */}

                        {modalMode === 'Approval' ? (
                            <ModalApprovalContent
                                isOpen={modalOpen}
                                onClose={closeModal}
                                modalData={modalData}
                                modalState={modalState}
                                darkMode={darkMode}
                                mode={modalMode}
                                onChange={async (newState) => {
                                    if (newState.submit) {
                                        try {
                                            setIsSaving(true);
                                            const invoiceId =
                                                modalData.invoiceId ||
                                                modalData.id;
                                            const previousStatus =
                                                modalData.invoiceStatus;
                                            const status =
                                                modalState && modalState.upload
                                                    ? modalState.upload
                                                    : newState.upload;

                                            await axios.put(
                                                `/invoice/${invoiceId}/${status}`,
                                            );
                                            const prevLabel =
                                                config.invoices.statusJSON[
                                                    previousStatus
                                                ] ?? previousStatus;
                                            const newLabel =
                                                config.invoices.statusJSON[
                                                    status
                                                ] ?? status;

                                            toast({
                                                title: 'Status Updated',
                                                description: `Invoice status changed from ${prevLabel} to ${newLabel}.`,
                                                status: 'success',
                                                duration: 2000,
                                                isClosable: true,
                                            });
                                            closeModal();
                                            refetch();
                                        } catch (err) {
                                            setApiError(
                                                'Failed to update invoice status.',
                                            );
                                        } finally {
                                            setIsSaving(false);
                                        }
                                    } else {
                                        setModalState((prev) => ({
                                            ...prev,
                                            ...newState,
                                        }));
                                    }
                                }}
                                isLoading={isSaving}
                            />
                        ) : modalMode === 'Delete' ? (
                            <Box>
                                <Box>
                                    <ItemCodeDisplay
                                        darkMode={darkMode}
                                        value={modalData?.itemCode}
                                    />
                                    <DescriptionDisplay
                                        darkMode={darkMode}
                                        value={modalData?.description}
                                    />
                                    <QuantityDisplay
                                        darkMode={darkMode}
                                        value={modalData?.quantity}
                                    />
                                    <UnitPriceDisplay
                                        darkMode={darkMode}
                                        value={modalData?.unitPrice}
                                    />
                                    <LineTotalDisplay
                                        darkMode={darkMode}
                                        value={modalData?.lineTotal}
                                    />
                                </Box>
                                <Box mb={4}>
                                    <Box
                                        bg="red.50"
                                        color="red.700"
                                        p={2}
                                        borderRadius="md"
                                        display="flex"
                                        alignItems="center">
                                        <span style={{ marginRight: 8 }}>
                                            ⚠️
                                        </span>
                                        Are you sure you want to delete this
                                        invoice line? This action cannot be
                                        undone.
                                    </Box>
                                </Box>
                            </Box>
                        ) : null}

                        {/* )} */}
                    </>
                )}
            </>
        </>
    );
};

export default InvoiceLines;
